export type WorkflowStep = {
  number: number;
  title: string;
  body: string;
};

export type ClarifyQuestion = {
  question: string;
  options: string[];
  multi: boolean;
};

export type BotMessageBlock =
  | { type: 'markdown'; content: string }
  | { type: 'steps'; steps: WorkflowStep[] }
  | { type: 'clarify'; questions: ClarifyQuestion[] };

const STEPS_TAG_REGEX = /<steps>([\s\S]*?)<\/steps>/gi;
const CLARIFY_TAG_REGEX = /<clarify>([\s\S]*?)<\/clarify>/gi;
const INLINE_CLARIFY_REGEX = /<clarify>([\s\S]*?)<\/clarify>/gi;

type StepsPayload = {
  steps?: Array<{ title?: unknown; description?: unknown }>;
};

type ClarifyPayload = {
  v?: unknown;
  questions?: Array<{ question?: unknown; options?: unknown; multi?: unknown }>;
};

type StructuredSpan = {
  start: number;
  end: number;
  block: Extract<BotMessageBlock, { type: 'steps' | 'clarify' }>;
};

function parseStepsPayload(raw: string): WorkflowStep[] | null {
  try {
    const data = JSON.parse(raw.trim()) as StepsPayload;
    if (!Array.isArray(data.steps) || data.steps.length === 0) return null;

    const steps = data.steps
      .map((step, index) => {
        if (typeof step?.title !== 'string' || !step.title.trim()) return null;
        return {
          number: index + 1,
          title: step.title.trim(),
          body: typeof step.description === 'string' ? step.description.trim() : '',
        };
      })
      .filter((step): step is WorkflowStep => step !== null);

    return steps.length > 0 ? steps : null;
  } catch {
    return null;
  }
}

function parseClarifyPayload(raw: string): ClarifyQuestion[] | null {
  try {
    const data = JSON.parse(raw.trim()) as ClarifyPayload;
    if (!Array.isArray(data.questions) || data.questions.length === 0) return null;

    const questions = data.questions
      .map(item => {
        if (typeof item?.question !== 'string' || !item.question.trim()) return null;
        if (!Array.isArray(item.options)) return null;

        const options = item.options
          .filter((option): option is string => typeof option === 'string')
          .map(option => option.trim())
          .filter(Boolean);

        if (options.length === 0) return null;

        return {
          question: item.question.trim(),
          options,
          multi: item.multi === true,
        };
      })
      .filter((item): item is ClarifyQuestion => item !== null);

    return questions.length > 0 ? questions : null;
  } catch {
    return null;
  }
}

function payloadToBlock(raw: string): Extract<BotMessageBlock, { type: 'steps' | 'clarify' }> | null {
  const steps = parseStepsPayload(raw);
  if (steps) return { type: 'steps', steps };

  const questions = parseClarifyPayload(raw);
  if (questions) return { type: 'clarify', questions };

  return null;
}

function pushMarkdown(blocks: BotMessageBlock[], content: string) {
  const trimmed = content.trim();
  if (trimmed) blocks.push({ type: 'markdown', content: trimmed });
}

export function decodeStructuredTagEntities(text: string): string {
  return text
    .replace(/&lt;steps&gt;/gi, '<steps>')
    .replace(/&lt;\/steps&gt;/gi, '</steps>')
    .replace(/&lt;clarify&gt;/gi, '<clarify>')
    .replace(/&lt;\/clarify&gt;/gi, '</clarify>');
}

function findJsonObjectSpans(text: string): Array<{ start: number; end: number; raw: string }> {
  const spans: Array<{ start: number; end: number; raw: string }> = [];

  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== '{') continue;

    let depth = 0;
    let inString = false;
    let escape = false;

    for (let j = i; j < text.length; j += 1) {
      const char = text[j];

      if (inString) {
        if (escape) {
          escape = false;
        } else if (char === '\\') {
          escape = true;
        } else if (char === '"') {
          inString = false;
        }
        continue;
      }

      if (char === '"') {
        inString = true;
        continue;
      }

      if (char === '{') {
        depth += 1;
      } else if (char === '}') {
        depth -= 1;
        if (depth === 0) {
          spans.push({ start: i, end: j + 1, raw: text.slice(i, j + 1) });
          i = j;
          break;
        }
      }
    }
  }

  return spans;
}

function collectRegexSpans(
  text: string,
  regex: RegExp,
  toBlock: (raw: string) => Extract<BotMessageBlock, { type: 'steps' | 'clarify' }> | null,
): StructuredSpan[] {
  const spans: StructuredSpan[] = [];
  const re = new RegExp(regex.source, 'gi');
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    const raw = match[1];
    if (raw == null) continue;

    const block = toBlock(raw);
    if (!block) continue;

    spans.push({
      start: match.index,
      end: match.index + match[0].length,
      block,
    });
  }

  return spans;
}

function collectTaggedSpans(text: string): StructuredSpan[] {
  return [
    ...collectRegexSpans(text, STEPS_TAG_REGEX, raw => {
      const steps = parseStepsPayload(raw);
      return steps ? { type: 'steps', steps } : null;
    }),
    ...collectRegexSpans(text, CLARIFY_TAG_REGEX, raw => {
      const questions = parseClarifyPayload(raw);
      return questions ? { type: 'clarify', questions } : null;
    }),
  ];
}

function collectBareJsonSpans(text: string, occupied: StructuredSpan[]): StructuredSpan[] {
  const spans: StructuredSpan[] = [];

  for (const candidate of findJsonObjectSpans(text)) {
    const overlaps = occupied.some(span => candidate.start < span.end && candidate.end > span.start);
    if (overlaps) continue;

    const block = payloadToBlock(candidate.raw);
    if (!block) continue;

    spans.push({
      start: candidate.start,
      end: candidate.end,
      block,
    });
  }

  return spans;
}

function buildBlocksFromSpans(text: string, spans: StructuredSpan[]): BotMessageBlock[] {
  const ordered = [...spans].sort((a, b) => a.start - b.start);
  const blocks: BotMessageBlock[] = [];
  let lastIndex = 0;

  for (const span of ordered) {
    if (span.start < lastIndex) continue;
    pushMarkdown(blocks, text.slice(lastIndex, span.start));
    blocks.push(span.block);
    lastIndex = span.end;
  }

  pushMarkdown(blocks, text.slice(lastIndex));
  return blocks;
}

function stripIncompleteTag(text: string, tag: 'steps' | 'clarify'): string {
  const open = `<${tag}>`;
  const close = `</${tag}>`;
  const lastOpen = text.toLowerCase().lastIndexOf(open);
  if (lastOpen === -1) return text;

  const tail = text.slice(lastOpen);
  if (tail.toLowerCase().includes(close)) return text;

  return text.slice(0, lastOpen);
}

function stripBareStructuredJson(text: string): string {
  const spans = collectBareJsonSpans(text, []);
  if (spans.length === 0) return text;

  let result = '';
  let lastIndex = 0;

  for (const span of spans.sort((a, b) => a.start - b.start)) {
    result += text.slice(lastIndex, span.start);
    lastIndex = span.end;
  }

  result += text.slice(lastIndex);
  return result;
}

export function stripStructuredTagsForStream(text: string): string {
  const decoded = decodeStructuredTagEntities(text);

  let result = decoded.replace(/<steps>[\s\S]*?<\/steps>/gi, '');
  result = result.replace(new RegExp(CLARIFY_TAG_REGEX.source, 'gi'), (full, inner: string) =>
    parseClarifyPayload(inner) ? '' : full,
  );
  result = stripIncompleteTag(result, 'steps');

  const incompleteClarify = result.toLowerCase().lastIndexOf('<clarify>');
  if (incompleteClarify !== -1) {
    const tail = result.slice(incompleteClarify + '<clarify>'.length);
    if (!tail.toLowerCase().includes('</clarify>') && /^\s*\{/.test(tail)) {
      result = result.slice(0, incompleteClarify);
    }
  }

  return stripBareStructuredJson(result);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function transformInlineClarifyTags(text: string): string {
  return text.replace(INLINE_CLARIFY_REGEX, (full, inner: string) => {
    const trimmed = inner.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('{')) return full;

    return `<span class="aida-clarify-topic">${escapeHtml(trimmed)}</span>`;
  });
}

export function unwrapSuggestedQuestionTags(text: string): string {
  return text.replace(/<q>([\s\S]*?)<\/q>/gi, '$1');
}

export function splitBotMessageBlocks(text: string): BotMessageBlock[] {
  const source = decodeStructuredTagEntities(text.replace(/\r\n/g, '\n'));
  const tagged = collectTaggedSpans(source);
  const bare = collectBareJsonSpans(source, tagged);
  return buildBlocksFromSpans(source, [...tagged, ...bare]);
}

export function hasClarifyPayloadPending(text: string): boolean {
  const source = decodeStructuredTagEntities(text);
  if (/<clarify>\s*\{/i.test(source)) return true;
  return collectBareJsonSpans(source, []).some(span => span.block.type === 'clarify');
}

export function hasStepsPayloadPending(text: string): boolean {
  const source = decodeStructuredTagEntities(text);
  if (/<steps>\s*\{/i.test(source)) return true;
  return collectBareJsonSpans(source, []).some(span => span.block.type === 'steps');
}
